import { SagaIterator } from "redux-saga";
import {
    put,
    call,
    takeLatest,
    select,
} from "redux-saga/effects";
import { fetchLogin, fetchUser } from "@montana/services/authentication";
import { isBrowser } from "@montana/helpers/browser.helper";
import {
    AuthenticationAction, loginSuccess, ILoginAction, loginError, setUser,
} from "./authentication.actions";
import { setToken, deleteTokens } from "@montana/helpers/auth-token.helper";
import {  IMarket } from "@montana/models/generated/cms.interface";
import { staticSettings } from '@montana/helpers/static-settings.helper';
import { push } from "connected-react-router";
import { getSessionStorageItem, removeSessionStorageItem } from "@montana/helpers/sessionstorage.helper";
import { REDIRECT_ON_LOGIN } from "@montana/constants/sessionstorage";
import { setLanguageAndMarket } from "../settings/settings.actions";
import { connectServiceFunctions } from "../base/base.sagas";
import { IStoreState } from "@montana/store/store";
import { IDynamicSettingsContainerState } from "../dynamic-settings/dynamic-settings";

export function* getUser(): SagaIterator {
    try {
        const [cFetchUser]: any[] = yield connectServiceFunctions(fetchUser);
        const response: Response = yield call(cFetchUser);
        const {
            json, ok,
        } = response;

        if (!ok) {
            return;
        }

        const user: any = yield call(json.bind(response));
        return user;

    } catch (error) {
        // TODO: Notify
        console.error('Could not fetch user');
    }
}

function* setSelectedMarket(markets?: IMarket[]): any {
    if (!markets) {
        return
    }

    const selectedMarket = markets.find(market => market.isSelected) || markets[0];
    // This is used for testing things on the default market
    //const selectedMarket = markets.find(market => market.marketId === 'EN') || markets[0];
    yield put(setLanguageAndMarket(undefined, selectedMarket));

    return selectedMarket;
}

export function* initialSetup(): SagaIterator {
    try {
        const { markets }: IDynamicSettingsContainerState = yield select((s: IStoreState) => s.dynamicSettings);
        const selectedMarket: IMarket | undefined = yield setSelectedMarket(markets);

        if (staticSettings.b2b) {
            const user: any | undefined = yield call(getUser);

            if (!user) return;

            if (user.marketId && user.marketId !== selectedMarket?.marketId) {
                const urlMarket = markets?.find(market => market.marketId === user.marketId)?.urlMarket;
                location.href = `/${urlMarket}`;
                return true;
            } else {
                yield put(setUser(user));
            }
        }

        return false;
    } catch (error) {
        console.error('Initial setup failed');
    }
}

export interface ILoginResponse {
    access_token: string;
    expires_in: number;
    refresh_token: string;
    token_type: string;
}

export function* onLogin(action: ILoginAction): SagaIterator {

    try {
        const { username, password, rememberMe } = action;

        const [cFetchLogin]: any[] = yield connectServiceFunctions(fetchLogin);
        const response = yield call(cFetchLogin, username, password);
        const {
            json, ok,
        } = response as unknown as Response;

        if (!ok) {
            throw new Error(`Couldn't authenticate user`);
        }

        const responseBody = yield call(json.bind(response));

        if (responseBody) {
            setToken(responseBody, rememberMe);
        }

        const redirectPath = getSessionStorageItem(REDIRECT_ON_LOGIN);

        const isHardRedirect = yield call(initialSetup);

        if (isHardRedirect) {
            return;
        }

        const { links }: IDynamicSettingsContainerState = yield select((s: IStoreState) => s.dynamicSettings);

        if (redirectPath != null) {
            yield put(push(redirectPath));
            removeSessionStorageItem(REDIRECT_ON_LOGIN);
        } else if (links?.homePage) {
            yield put(push(links?.homePage));
        }

        yield put(loginSuccess());

    } catch (e) {
        // If a token is present for whatever reason, make sure it is deleted as the login process has now failed.
        deleteTokens();
        yield put(loginError());
    }
}

export function* onLogout(): SagaIterator {
    deleteTokens();

    if (!isBrowser) { return; }

    const { links }: IDynamicSettingsContainerState = yield select((s: IStoreState) => s.dynamicSettings);

    if (links?.login) {
        document.location.href = `${links?.login}`
    }
}

export const authenticationSagas = [
    takeLatest(AuthenticationAction.LOGIN, onLogin),
    takeLatest(AuthenticationAction.LOGOUT, onLogout),
];
